import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { createV2Flow, studioV2NewFlowDraft } from '../../../api/studioV2.api';
import { STUDIO_V2_FLOW_TEMPLATES } from '../studioV2FlowTemplates';
import { normalizeV2FlowEdge } from '../studioV2FlowModel';
import FlowMiniDiagram from './FlowMiniDiagram';

export const FRIENDLY_FLOW_TEMPLATES = {
  basic: { label: 'Simple flow', description: 'One path from start to finish. A good first flow.' },
  conditional: { label: 'Yes / no branch', description: 'Take a different path depending on a condition.' },
  api: { label: 'Call a web service', description: 'Fetch data from another system and use it.' },
  'human-input': { label: 'Human approval', description: 'Pause for someone to approve or reply, then continue.' },
  parallel: { label: 'Do things in parallel', description: 'Run two branches at once and combine the results.' },
  'candidate-screening': { label: 'Candidate screening', description: 'Check an application and score it with AI.' },
  'interview-scorecard': { label: 'Interview scorecard', description: 'Turn interview notes into a summary and scores.' },
  'application-type-advisor': { label: 'SF application advisor', description: 'CLAW tool recommends permit, planning, or license.' },
};

function StartCard({
  selected, onClick, title, description, visual, testId,
}) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testId}
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 2.5,
        border: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        gap: 1.25,
        height: '100%',
      })}
    >
      {visual}
      <Box>
        <Typography variant="body2" fontWeight={700}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{description}</Typography>
      </Box>
    </ButtonBase>
  );
}

function IconVisual({ Icon, color }) {
  return (
    <Box sx={(theme) => ({
      height: 96,
      borderRadius: 2,
      display: 'grid',
      placeItems: 'center',
      bgcolor: alpha(theme.palette[color].main, 0.06),
      color: `${color}.main`,
    })}
    >
      <Icon sx={{ fontSize: 36 }} />
    </Box>
  );
}

/** Guided "New flow": blank, from a template, or described to the AI; then a name and description. */
export default function NewFlowWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [startId, setStartId] = useState('blank');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setStartId('blank');
    setName('');
    setDescription('');
    setPrompt('');
    setError(null);
  }, [open]);

  const template = STUDIO_V2_FLOW_TEMPLATES.find((t) => t.id === startId) || null;
  const isAi = startId === 'ai';
  const defaultName = template ? (FRIENDLY_FLOW_TEMPLATES[template.id]?.label || template.label) : 'Untitled flow';

  const buildDefinition = () => {
    const finalName = name.trim() || defaultName;
    const draft = studioV2NewFlowDraft(finalName);
    if (!template) {
      return { ...draft, description: description.trim() || (isAi ? prompt.trim().slice(0, 240) : '') };
    }
    const tpl = template.definition;
    return {
      ...tpl,
      id: draft.id,
      name: finalName,
      description: description.trim() || tpl.description || '',
      schemaVersion: 'flow/v2',
      edges: (Array.isArray(tpl.edges) ? tpl.edges : []).map((e) => normalizeV2FlowEdge(e)),
      metadata: { ...(tpl.metadata || {}), lastTemplateId: template.id, lastTemplateAt: new Date().toISOString() },
    };
  };

  const onCreate = async () => {
    if (isAi && !prompt.trim()) {
      setError('Describe what the flow should do first.');
      return;
    }
    setCreating(true);
    setError(null);
    const { flow, error: err } = await createV2Flow(buildDefinition());
    setCreating(false);
    if (err || !flow?._id) {
      setError(err || 'The flow was not created. Please try again.');
      return;
    }
    onCreated(flow, { assistantPrompt: isAi ? prompt.trim() : '' });
  };

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ 'data-testid': 'new-flow-wizard' }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        {step === 0 ? 'New flow' : 'Name your flow'}
        <Typography variant="body2" color="text.secondary">
          {step === 0 ? 'How would you like to start?' : 'You can change these any time in Settings.'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {step === 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
            <StartCard
              selected={startId === 'blank'}
              onClick={() => setStartId('blank')}
              title="Start from scratch"
              description="An empty canvas with Start and Finish steps."
              visual={<IconVisual Icon={AddIcon} color="primary" />}
              testId="flow-start-blank"
            />
            <StartCard
              selected={isAi}
              onClick={() => setStartId('ai')}
              title="Describe it to the AI"
              description="Say what the flow should do and the assistant drafts it."
              visual={<IconVisual Icon={AutoAwesomeIcon} color="secondary" />}
              testId="flow-start-ai"
            />
            {STUDIO_V2_FLOW_TEMPLATES.map((tpl) => {
              const copy = FRIENDLY_FLOW_TEMPLATES[tpl.id] || { label: tpl.label, description: tpl.description };
              return (
                <StartCard
                  key={tpl.id}
                  selected={startId === tpl.id}
                  onClick={() => setStartId(tpl.id)}
                  title={copy.label}
                  description={copy.description}
                  visual={<FlowMiniDiagram definition={tpl.definition} height={96} />}
                  testId={`flow-start-${tpl.id}`}
                />
              );
            })}
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ maxWidth: 560 }}>
            {isAi && (
              <TextField
                autoFocus
                label="What should this flow do?"
                placeholder="For example: read a candidate's CV, score it against the job, and suggest whether to invite them to interview"
                multiline
                minRows={4}
                fullWidth
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                inputProps={{ 'data-testid': 'flow-wizard-prompt' }}
              />
            )}
            <TextField
              autoFocus={!isAi}
              label="Name"
              placeholder={defaultName}
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ 'data-testid': 'flow-wizard-name' }}
            />
            {!isAi && (
              <TextField
                label="Description (optional)"
                placeholder="What does this flow do?"
                multiline
                minRows={2}
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 1 && <Button onClick={() => setStep(0)} disabled={creating} sx={{ mr: 'auto' }}>Back</Button>}
        <Button onClick={onClose} disabled={creating}>Cancel</Button>
        {step === 0 ? (
          <Button variant="contained" onClick={() => setStep(1)} data-testid="flow-wizard-next">Next</Button>
        ) : (
          <Button
            variant="contained"
            onClick={onCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
            data-testid="flow-wizard-create"
          >
            {isAi ? 'Create and open the assistant' : 'Create flow'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
